import { fmtDate } from "../format";
import type { RouteRow } from "../types";

/** The replay-status meta suffix — five values plus null, each its own
 *  reading (§5.2): the last three render as themselves, never as a failure
 *  and never as a pass; null means the latest run never judged this route. */
function replaySuffix(route: RouteRow): React.ReactNode {
  if (route.replayStatus === null) {
    return <span className="text-gray-400">not yet replayed</span>;
  }
  switch (route.replayStatus) {
    case "reproduced":
      return <span className="text-success-dark">replayed weekly ✓ reproduced</span>;
    case "chain-diverged":
      return (
        <span className="text-danger-dark">
          ✗ chain diverged — escalated to discovery walk
        </span>
      );
    case "not-applicable":
      return <span className="text-gray-500">replay not-applicable</span>;
    case "not-checkable":
      return <span className="text-warning-dark">replay not-checkable</span>;
    case "not-run":
      return <span className="text-gray-500">replay not run</span>;
  }
}

/** One booking route: the domain chain (first element is the entry hop) and
 *  the fingerprint meta line. */
export function RouteChain({ route }: { route: RouteRow }) {
  return (
    <div className="py-2.5 border-b border-[#E9E9EB] last:border-b-0">
      <div className="text-sm text-content-primary font-medium flex items-center gap-1.5 flex-wrap">
        {route.domainChain.map((domain, i) => (
          <span key={`${domain}-${i}`} className="inline-flex items-center gap-1.5">
            {i > 0 && <span className="text-gray-400">→</span>}
            <span className={i === 0 ? "" : "text-content-secondary"}>{domain}</span>
          </span>
        ))}
      </div>
      <div className="text-xs text-gray-500 mt-1">
        fingerprint written {fmtDate(route.writtenAt)} · depth last{" "}
        {route.depthLastReached} / ever {route.depthEverReached}
        {route.lastReplayAt && <> · last replay {fmtDate(route.lastReplayAt)}</>}{" "}
        · {replaySuffix(route)}
      </div>
    </div>
  );
}
